import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Checkbox,
  IconButton,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { colors } from "../../../theme/colors";
import { Form, Field } from "react-final-form";
import BITextFieldForm from "../../../components/common/Forms/TextField/TextField";
import {
  required,
  composeValidators,
  validateEmail,
  validateNIT,
  onlyNumbers,
  onlyText,
  validateCvv,
  cardLength,
  phoneNumber,
  validateExpirationDate,
} from "../../../utils/validatorsEn";
import { useNavigate } from "react-router-dom";
import { NavigateBefore } from "@mui/icons-material";

import {
  cc_expires_format,
  getInitials,
  letters_format,
  numbers_only_format,
} from "../../../utils/functions";

const SectionHeader = ({ title }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      my: 3,
      width: "100%",
    }}
  >
    <Divider
      sx={{
        flex: 1,
        backgroundColor: "#E0E0E0",
        height: "1px",
      }}
    />
    <Box
      sx={{
        mx: 0,
        textAlign: "center",
        backgroundColor: "white",
        px: 4,
        py: 1,
        borderRadius: "12px",
        border: "1px solid #E0E0E0",
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 700,
          fontSize: "1rem",
          color: "#00216A",
        }}
      >
        {title}
      </Typography>
    </Box>
    <Divider
      sx={{
        flex: 1,
        backgroundColor: "#E0E0E0",
        height: "1px",
      }}
    />
  </Box>
);

const ContentWrapper = ({ children }) => (
  <Box
    sx={{
      maxWidth: "735px",
      margin: "0 auto",
      padding: "0 1rem",
    }}
  >
    {children}
  </Box>
);

export default function NewPay({ prevStep }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { amount } = location.state || {};
  const totalAmount = amount ? parseFloat(amount).toFixed(2) : "0.00";

  const [cfSelected, setCfSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const errorAlertRef = useRef(null);

  useEffect(() => {
    if (error && errorAlertRef.current) {
      errorAlertRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [error]);

  const handleBack = () => {
    if (prevStep) {
      prevStep();
    } else {
      navigate(-1);
    }
  };

  const handleSelectCF = (e) => {
    setCfSelected(e.target.checked);
  };

  const handleSubmit = async (formProps) => {
    if (isLoading) return;

    setIsLoading(true);
    setError("");

    const payload = {
      payment: {
        cardNumber: formProps.numeroTarjeta,
        cardHolder: formProps.nombreEnTarjeta,
        expDate: formProps.fechaExpiracion,
        cvv: formProps.cvv,
      },
      taxpayer: {
        nit: cfSelected ? "CF" : formProps.nit,
        name: `${formProps.nombre} ${formProps.apellido}`,
        phone: formProps.telefono,
        email: formProps.correoBoleta,
      },
      amount: parseFloat(totalAmount),
    };

    try {
      const apiUrl = process.env.REACT_APP_BE_URL;

      if (!apiUrl) {
        throw new Error("API URL not configured");
      }

      const response = await fetch(`${apiUrl}/inspiraairlines/donate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Payment successful:", result);

        navigate("/en/result", {
          state: {
            paymentResult: result,
            amount: totalAmount,
            paymentData: payload,
            timestamp: Date.now(),
          },
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message ||
          `Error ${response.status}: ${response.statusText}`;
        console.error("Payment error:", errorMessage);
        setError(`Error processing payment: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Request error:", error);
      setError(
        error.message === "API URL not configured"
          ? "Configuration error. Contact the administrator."
          : "Connection error. Check your internet connection and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        backgroundColor: colors?.white?.main || "#ffffff",
        paddingY: "20px",
      }}
    >
      <Container maxWidth="md">
        <ContentWrapper>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              position: "relative",
            }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                position: "absolute",
                left: 0,
              }}
              aria-label="Back"
            >
              <NavigateBefore
                sx={{
                  color: colors?.primary || "#1976d2",
                  fontSize: "30px",
                }}
              />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: colors?.primary || "#1976d2",
                fontWeight: "bold",
                fontSize: { xs: "18px", sm: "22px", md: "28px" },
              }}
            >
              Purchase Details
            </Typography>
          </Box>

          <Box
            sx={{
              textAlign: "center",
              mb: 3,
              display: "flex",
              gap: "33px",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: colors?.primary,
                mb: 1,
                fontSize: "24px",
              }}
            >
              Total to pay
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: colors?.primary,
                mb: 1,
                fontSize: "24px",
              }}
            >
              Q{totalAmount}
            </Typography>
          </Box>

          {error && (
            <Alert
              ref={errorAlertRef}
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit} initialValues={{}}>
            {({ handleSubmit, valid, form }) => (
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="nombre"
                      component={BITextFieldForm}
                      label="First Name"
                      required
                      validate={composeValidators(
                        required,
                        onlyText("Invalid field"),
                      )}
                      placeholder="John"
                      format={(value) =>
                        value ? letters_format(String(value)) : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="apellido"
                      component={BITextFieldForm}
                      label="Last Name"
                      required
                      validate={composeValidators(
                        required,
                        onlyText("Invalid field"),
                      )}
                      placeholder="Doe"
                      format={(value) =>
                        value ? letters_format(String(value)) : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="telefono"
                      component={BITextFieldForm}
                      label="Phone"
                      required
                      validate={composeValidators(
                        required,
                        phoneNumber("Invalid phone number"),
                      )}
                      placeholder="12345678"
                      format={(value) => numbers_only_format(String(value))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="correo"
                      component={BITextFieldForm}
                      label="Email"
                      required
                      validate={composeValidators(
                        required,
                        validateEmail("Invalid email"),
                      )}
                      placeholder="Enter your email"
                    />
                  </Grid>
                </Grid>

                <SectionHeader title="Card Details" />

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      color: "#003865",
                      fontSize: "16px",
                    }}
                  >
                    Complete your purchase
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#4F5151",
                      fontSize: "14px",
                      mb: 2,
                    }}
                  >
                    Pay with a credit card and remember not to share your card
                    information with anyone
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="numeroTarjeta"
                      component={BITextFieldForm}
                      label="Card Number"
                      required
                      validate={composeValidators(
                        required,
                        onlyNumbers("Invalid number"),
                        cardLength("Must be 16 to 17 characters"),
                      )}
                      placeholder="0000 0000 0000 0000"
                      format={(value) =>
                        value ? numbers_only_format(String(value)) : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="nombreEnTarjeta"
                      component={BITextFieldForm}
                      label="Name on card"
                      required
                      validate={composeValidators(
                        required,
                        onlyText("Invalid field"),
                      )}
                      placeholder="John Doe"
                      format={(value) =>
                        value ? letters_format(String(value)) : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="fechaExpiracion"
                      component={BITextFieldForm}
                      label="Expiration Date"
                      required
                      placeholder="00/00"
                      validate={composeValidators(
                        required,
                        validateExpirationDate,
                      )}
                      format={(value) => cc_expires_format(String(value))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="cvv"
                      component={BITextFieldForm}
                      label="CVV"
                      required
                      validate={composeValidators(required, validateCvv)}
                      placeholder="000"
                      format={(value) =>
                        value ? numbers_only_format(String(value)) : ""
                      }
                    />
                  </Grid>
                </Grid>

                <SectionHeader title="Billing Information" />

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="nit"
                      component={BITextFieldForm}
                      label="TIN"
                      required={!cfSelected}
                      placeholder="00000000-0"
                      validate={
                        !cfSelected
                          ? composeValidators(required, validateNIT)
                          : null
                      }
                      disabled={cfSelected}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={parseFloat(totalAmount) >= 2500}
                          onChange={(e) => {
                            handleSelectCF(e);
                            if (e.target.checked) form.resetFieldState("nit");
                          }}
                        />
                      }
                      label="Final Consumer (CF)"
                    />
                  </Grid>
                </Grid>

                <SectionHeader title="Additional Information" />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      name="correoBoleta"
                      component={BITextFieldForm}
                      label="Electronic ticket delivery"
                      required
                      validate={composeValidators(
                        required,
                        validateEmail("Invalid email"),
                      )}
                      placeholder="Enter an email address"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={0} justifyContent="center">
                  <Grid item xs={12} md={9}>
                    <Button
                      color="primary"
                      variant="contained"
                      sx={{
                        width: "100%",
                        borderRadius: "8px",
                        boxShadow: "none",
                        backgroundColor: colors?.primary || "#1976d2",
                        color: colors?.white?.main || "#ffffff",
                        textTransform: "none",
                        fontWeight: 700,
                        height: "48px",
                        marginTop: "2rem",
                        "&:disabled": {
                          backgroundColor: "#ccc",
                          color: "#666",
                          cursor: "not-allowed",
                        },
                        "&:hover:not(:disabled)": {
                          backgroundColor: colors?.primary || "#1976d2",
                          opacity: 0.9,
                        },
                      }}
                      type="submit"
                      disabled={!valid || isLoading}
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                    >
                      {isLoading ? "Processing..." : "Next"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Form>

          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "#00216A",
              fontSize: "1rem",
              marginTop: "2rem",
            }}
          >
            2026 © All rights reserved.
          </Typography>
        </ContentWrapper>
      </Container>
    </Box>
  );
}
